import React from "react";
import { Badge } from "../ui/badge";

// ============================================================================
// StatusBadge — SATU-SATUNYA komponen badge status ETAMSIOS (#4b-1, A1/A2).
//
// Sebelum #4b-1 ada 7 peta warna status yang saling duplikat:
// 4 di file ini (FA/PR/Exception/Deadline) + PR_STATUS_BADGE di
// pages/purchasing/PurchaseRequests.jsx + TONE_CLS di PurchaseOrders.jsx +
// TONE_CLS di Receiving.jsx. Keputusan Q68 melebur WARNA-nya ke sini — tapi
// peta PR_STATUS_BADGE sendiri TETAP TINGGAL di halamannya sampai Batch 13
// memindahkannya (lihat PURCHASE_REQUEST_STATUS_TONE di bawah). Kalimat
// "semuanya dilebur ke sini" baru benar sejak Batch 13, bukan sejak Q68.
//
// ATURAN (STANDAR_UI A2): warna TIDAK ditulis literal — semua dari token
// (bg-success-soft dst., lihat index.css). Halaman dilarang membuat peta
// warna sendiri; kalau butuh tone baru, tambahkan DI SINI.
//
// Tone resmi (A2 — satu warna satu makna). Kamus rona K73 + K81 berlaku
// LINTAS APLIKASI, bukan per layar:
//   info     = BIRU   — sedang berjalan
//   success  = HIJAU  — selesai
//   warning  = KUNING — menunggu tindakan orang
//   danger   = MERAH  — ditolak / bermasalah
//   neutral  = ABU    — TIDAK sedang berjalan. Mencakup DUA keadaan:
//              belum mulai (Draft) DAN sudah berakhir tanpa hasil
//              (dibatalkan / dicabut / dihapus-buku / dilewati).
//
// K81 sengaja TIDAK menambah rona keenam untuk "sudah berakhir": yang
// membedakan kedua keadaan itu LABEL, bukan warna. Makin sedikit rona,
// makin cepat dibaca orang produksi.
//
// Varian *-solid dipakai untuk penekanan kuat (mis. "Lunas").
// ============================================================================

export const TONE_CLASSES = {
  success: "bg-success-soft text-success-soft-foreground",
  "success-solid": "bg-success text-success-foreground",
  warning: "bg-warning-soft text-warning-soft-foreground",
  "warning-solid": "bg-warning text-warning-foreground",
  danger: "bg-danger-soft text-danger-soft-foreground",
  "danger-solid": "bg-danger text-danger-foreground",
  info: "bg-info-soft text-info-soft-foreground",
  "info-solid": "bg-info text-info-foreground",
  neutral: "bg-neutral-soft text-neutral-soft-foreground",
  "neutral-solid": "bg-neutral text-neutral-foreground",
  // Q68-01 Batch 7 (GAP-E) — dua tone TAMBAHAN di luar lima tone A2, dipakai
  // HANYA modul Reject/Remake. Bukan pelanggaran "satu warna satu makna":
  // badge STATUS `REPORTED` dan badge TIPE `REMAKE` dirender bersebelahan di
  // baris yang sama, jadi memetakan keduanya ke `warning` membuat satu baris
  // punya dua badge kembar yang tak terbedakan. Keduanya sengaja dinamai
  // menurut state-nya, bukan menurut makna — pengakuan jujur bahwa ini
  // pengecualian modul, bukan perluasan kosakata A2.
  //
  // JANGAN pakai keduanya di modul lain. Kalau modul lain butuh warna di luar
  // lima tone A2, itu pertanyaan untuk Owner, bukan alasan meminjam ini.
  reported: "bg-reported-soft text-reported-soft-foreground",
  remake: "bg-remake-soft text-remake-soft-foreground",
};

// Cincin sewarna, OPT-IN (Q68-01 Batch 2, GAP-C). Jalur keras
// `lib/statusLabels.js` memancarkan kelas cincin literal (emerald-200 dkk.) dan enam
// halaman pemakainya menambah kelas `border`; ini padanan tokennya, supaya
// halaman-halaman itu bisa pindah ke jalur token TANPA kehilangan cincinnya.
//
// SENGAJA peta terpisah, bukan digabung ke `TONE_CLASSES`: badge purchasing
// dan CustomerDetail hari ini TANPA cincin dan sudah lulus test. Menggabung
// akan mengubah 13 berkas yang tidak diminta berubah — persis yang dilarang
// batch ini. Dinyalakan per pemakai lewat prop `bordered`.
//
// Varian `-solid` sengaja TIDAK punya cincin: latarnya sudah pekat, cincin
// sewarna di atasnya tidak terlihat dan hanya menambah berat visual.
export const TONE_BORDER_CLASSES = {
  success: "border-success-soft-border",
  warning: "border-warning-soft-border",
  danger: "border-danger-soft-border",
  info: "border-info-soft-border",
  neutral: "border-neutral-soft-border",
  reported: "border-reported-soft-border",
  remake: "border-remake-soft-border",
};

// Tone yang dikirim backend lewat endpoint /meta (PO_LIFECYCLE: waiting/
// active/done/void; GRN: ok/warn/done/void) → tone token A2.
// Backend adalah sumber daftar status; UI hanya menerjemahkan tone-nya.
const META_TONE = {
  waiting: "warning",
  active: "info",
  done: "success",
  void: "neutral",
  ok: "success",
  warn: "warning",
  danger: "danger",
};

export function toneFromMeta(metaTone) {
  return META_TONE[metaTone] || "neutral";
}

/** Badge status generik. `tone` = kunci TONE_CLASSES. Teks status wajib
 *  ikut tampil (A2: dilarang mengandalkan warna saja).
 *
 *  `variant` (Q68-01 Batch 2, GAP-D) diteruskan apa adanya ke `Badge`.
 *  DEFAULT `undefined` — dan itu bukan kelalaian: `Badge` sendiri yang
 *  memutuskan default-nya lewat `defaultVariants` (`ui/badge.jsx:21`), jadi
 *  meneruskan `undefined` menghasilkan markup yang PERSIS sama dengan
 *  sebelum prop ini ada. Disediakan untuk `DesignRequests`/
 *  `DesignRequestDetail` yang hari ini memakai `variant="outline"`.
 *
 *  `bordered` (GAP-C) menambah cincin sewarna dari `TONE_BORDER_CLASSES`.
 *  DEFAULT `false` supaya 13 berkas yang sudah memakai komponen ini tidak
 *  berubah sepotong pun. */
export function StatusBadge({
  tone = "neutral",
  children,
  testId,
  className = "",
  variant,
  bordered = false,
}) {
  const cls = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
  const ring = bordered ? ` ${TONE_BORDER_CLASSES[tone] || TONE_BORDER_CLASSES.neutral}` : "";
  return (
    <Badge
      variant={variant}
      data-testid={testId}
      className={`${cls} hover:${cls.split(" ")[0]}${ring} ${className}`}
    >
      {children}
    </Badge>
  );
}

/** Badge dari entri status /meta backend: {code,label,tone}. Fallback aman
 *  kalau meta belum termuat: tampilkan kode apa adanya, tone netral. */
export function MetaStatusBadge({ meta, code, testId }) {
  const list = meta?.statuses || [];
  const entry = list.find((s) => s.code === code);
  return (
    <StatusBadge tone={entry ? toneFromMeta(entry.tone) : "neutral"} testId={testId}>
      {entry?.label || code || "—"}
    </StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// Peta lama (FA / payment_records / exception / deadline) — API dipertahankan,
// isinya dikonversi dari class literal ke tone token. Arti status TIDAK
// berubah; hanya sumber warnanya (Q68).
// ---------------------------------------------------------------------------

// Map FA order_status (12) → tone + label.
//
// FALABEL-01 / CUST-02. Peta lama di sini memetakan `payment_status` — kolom
// 9-state yang DIHAPUS migration 004 (`004_phase1b_correction_flow.sql:87`)
// dan diganti `order_status` 12-state. Kuncinya pun berupa kalimat Indonesia
// ("Menunggu DP"), bukan kode; sesudah 004 nol nilai yang pernah cocok, jadi
// setiap badge FA di CustomerDetail jatuh ke fallback netral.
//
// Sumber daftar 12 nilai ini adalah CHECK `fa_orders_order_status_check`
// (`004_phase1b_correction_flow.sql:104-117`) — DB, bukan peta JS mana pun.
// Empat di antaranya (`PELUNASAN_WAITING_CONFIRMATION`, `LUNAS`,
// `FREE_OF_CHARGE`, `EXCEPTION_PENDING_OWNER`) TIDAK punya label di
// `lib/statusLabels.js:FA_STATUS_LABEL`, dan `LUNAS` yang paling menggigit:
// state "sudah dibayar penuh" hari ini tampil sebagai kode mentah di
// FADetail/FAList.
//
// ⚠ DUA PETA LABEL SEKARANG HIDUP BERSAMAAN, dan itu disadari, bukan
// kelalaian: `FA_STATUS_LABEL` masih memuat 4 kunci mati (`CLOSED`,
// `PELUNASAN_CONFIRMED`, `QC_PASSED`, `READY_FOR_QC` — ditolak CHECK) dan
// tetap kehilangan 4 di atas. Merapikannya adalah unit kerja FALABEL-01
// tersendiri, yang menyentuh FADetail + FAList yang sudah live — BACKLOG
// menyatakan eksplisit ia tidak boleh diselipkan ke unit lain. Saat
// FALABEL-01 dikerjakan, peta INI yang jadi sumbernya, jangan lahirkan
// salinan ketiga.
//
// Label untuk 8 kunci yang beririsan disalin PERSIS dari `FA_STATUS_LABEL`
// supaya satu FA tidak pernah berbunyi beda antara CustomerDetail dan
// FADetail. Tone mengikuti A2 (menunggu = warning, berjalan = info,
// draft/batal = neutral, lunas = success-solid — A2 menyebut "Lunas" sebagai
// contoh penekanan kuatnya sendiri).
export const FA_ORDER_STATUS = {
  DRAFT_FA_CREATED: { tone: "neutral", l: "Draf FA Dibuat" },
  FA_WAITING_CUSTOMER_APPROVAL: { tone: "warning", l: "FA Menunggu Persetujuan Customer" },
  FA_APPROVED_WAITING_DP: { tone: "warning", l: "FA Disetujui — Menunggu DP" },
  DP_WAITING_CONFIRMATION: { tone: "warning", l: "DP Menunggu Konfirmasi" },
  DP_CONFIRMED_READY_FOR_WO: { tone: "neutral", l: "DP Dikonfirmasi — Siap WO" },
  IN_PRODUCTION: { tone: "info", l: "Sedang Produksi" },
  WAITING_PELUNASAN: { tone: "warning", l: "Menunggu Pelunasan" },
  PELUNASAN_WAITING_CONFIRMATION: { tone: "warning", l: "Pelunasan Menunggu Konfirmasi" },
  LUNAS: { tone: "success-solid", l: "Lunas" },
  // Serah terima ke customer (migration 20260909150000 menambah keduanya ke CHECK). Tanpa dua baris ini
  // FA yang sudah siap/diserahkan tampil "—" di daftar FA dan di riwayat Ruang Pesanan (galeri 27 Sep 2026).
  SIAP_DISERAHKAN: { tone: "success", l: "Siap Diserahkan" },
  DISERAHKAN: { tone: "success-solid", l: "Sudah Diserahkan" },
  FREE_OF_CHARGE: { tone: "success", l: "FOC (Gratis)" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
  // Tone-nya SENGAJA warning, bukan danger — sejajar dengan EXC_TONE.PENDING
  // di berkas yang sama, supaya satu keadaan tidak punya dua warna.
  EXCEPTION_PENDING_OWNER: { tone: "warning", l: "Pengecualian — Menunggu Persetujuan Owner" },
};

// payment_records.status
const PR_STATUS_TONE = {
  MENUNGGU_KONFIRMASI: { tone: "warning", l: "Menunggu Konfirmasi" },
  DIKONFIRMASI: { tone: "success-solid", l: "Dikonfirmasi" },
  DITOLAK: { tone: "danger", l: "Ditolak" },
  // K220 (BUSINESS-96): tautan DOKU lewat 24 jam tanpa dibayar — ditutup sistem, BUKAN ditolak siapa pun.
  KEDALUWARSA: { tone: "neutral", l: "Kedaluwarsa" },
};

// purchase_requests.status — DIPINDAH dari pages/purchasing/PurchaseRequests.jsx
// (Batch 13). Peta tone yang hidup di halaman adalah titik penegakan KEDUA, dan
// K73/K81 tidak bisa ditegakkan di satu tempat kalau kosakata ronanya tersebar.
//
// NAMANYA SENGAJA PANJANG. "PR" tepat di atas berarti PAYMENT RECORD, sementara
// "PR" di modul purchasing berarti PURCHASE REQUEST — dua kolom berbeda di dua
// tabel berbeda. Menamainya PR_STATUS_BADGE di sini akan menaruh dua arti "PR"
// bersebelahan, dan pembaca berikutnya tidak punya cara membedakannya.
//
// Bentuknya diseragamkan ke { tone, l } seperti 18 peta lainnya di berkas ini;
// di halaman asalnya ia { label, tone }. Peta yang bentuknya sendiri di tengah
// saudara-saudaranya adalah jebakan, bukan kompatibilitas.
//
// ⚠ `COMPLETED` DITAMBAHKAN (UAT7-T2-PR-COMPLETED-01). Sampai unit ini ia NOL
// ADA di peta, jadi PR yang sudah selesai jatuh ke fallback dan tampil sebagai
// KODE MENTAH ber-rona ABU — dua pelanggaran sekaligus: kode basis data sampai
// ke layar orang purchasing, dan K73 yang menetapkan selesai = HIJAU dibaca
// terbalik jadi "tidak sedang berjalan" (K81).
//
// Ia BUKAN status teoretis. Himpunannya TERTUTUP di enam, dibuktikan dua sumber
// otoritatif yang saling mengonfirmasi — bukan dibaca dari kode UI:
//   backend/migrations/013d_status_completed_pr.sql:26
//     check (status in ('DRAFT','SUBMITTED','APPROVED','REJECTED','CANCELLED','COMPLETED'))
//   backend/services/po_service.py:853-876  auto-complete: PR ikut COMPLETED
//     begitu PO-nya COMPLETED (1 PR = 1 PO)
// Ditambah sensus data NYATA di 025_pr_approval_limit_per_region.sql:58 —
// `COMPLETED 8` baris. Yang jatuh ke fallback bukan kemungkinan, melainkan
// delapan baris yang sudah ada.
//
// ⚠ Docstring `pr_service.py:4-6` masih menulis "APPROVED/REJECTED/CANCELLED
// terminal" — ia BASI sejak 013d dan bukan sumber yang dipakai di sini.
//
// URUTAN KUNCI BUKAN SELERA: `PurchaseRequests.jsx:175` menurunkan isi filter
// dropdown dari `Object.entries` peta ini, jadi urutan di sini ADALAH urutan
// yang dibaca orang di layar. Disusun menurut siklus hidup (DRAFT -> SUBMITTED
// -> APPROVED -> COMPLETED, lalu dua terminal), bukan abjad.
//
// Efek kedua yang ikut tertutup dan sebelumnya nol dilaporkan: sampai unit ini
// filter status TIDAK PUNYA pilihan "Selesai" sama sekali, jadi kedelapan PR
// itu tak bisa disaring — "present but empty" dari arah sebaliknya.
//
// ⚠ `COMPLETED` memakai `success-solid`, BUKAN `success` polos, dan itu
// keputusan dari MAKNA bukan dari selera. Di peta ini `APPROVED` BUKAN keadaan
// akhir — PO lahir otomatis darinya dan pengadaannya baru mulai berjalan,
// sementara `COMPLETED` berarti diterima penuh DAN lunas. Memberi keduanya
// `success` polos membuat PR yang MASIH berjalan tampil identik dengan yang
// sudah selesai — persis cacat yang `WO_STATUS_TONE` di berkas ini tolak
// eksplisit ("Tone dipilih dari MAKNA, bukan dari rona"), dan pemecahannya
// dipinjam bulat-bulat dari sana: DUA TINGKAT KEKUATAN dari keluarga tone yang
// sama, bukan rona keenam (K81 — nol rona baru).
//
// Bedanya dengan pasangan `LOCKED`/`IN_PROGRESS` di WO cuma arah: di sana yang
// solid adalah yang sedang berjalan, di sini yang solid adalah yang terminal —
// sejajar `FA_ORDER_STATUS.LUNAS`, yang A2 sebut sendiri sebagai contoh
// penekanan kuatnya. Rasio varian solid TIDAK diasumsikan lulus: ia sudah
// diukur dan diperbaiki di tokennya lewat `J3-SUCCESS-SOLID-01`.
//
// ⚠ NOL rona lama bergeser: `APPROVED` dibiarkan `success` apa adanya. Sapuan
// K73/K81 sudah menetapkannya, dan unit ini hanya MENAMBAH kunci yang hilang.
export const PURCHASE_REQUEST_STATUS_TONE = {
  DRAFT: { tone: "neutral", l: "Draf" },
  SUBMITTED: { tone: "warning", l: "Diajukan" },
  APPROVED: { tone: "success", l: "Disetujui" },
  COMPLETED: { tone: "success-solid", l: "Selesai" },
  REJECTED: { tone: "danger", l: "Ditolak" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// Exception status
const EXC_TONE = {
  NONE: { tone: "neutral", l: "—" },
  PENDING: { tone: "warning", l: "Menunggu Persetujuan Owner" },
  APPROVED: { tone: "success-solid", l: "Disetujui" },
  REJECTED: { tone: "danger", l: "Ditolak" },
};

/** Badge `fa_orders.order_status`. `value` = KODE dari DB (mis. `LUNAS`),
 *  bukan kalimat — kontraknya berubah bersama CUST-02. Kode tak dikenal
 *  tampil apa adanya, pola persis `StatusBadge` di FADetail.jsx:22 —
 *  drift DB dibiarkan terlihat, tidak disembunyikan jadi "—". */
export function FAStatusBadge({ value, testId }) {
  const t = FA_ORDER_STATUS[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

export function PRStatusBadge({ value, testId }) {
  const t = PR_STATUS_TONE[value] || { tone: "neutral", l: value };
  return (
    <StatusBadge tone={t.tone} testId={testId}>
      {t.l}
    </StatusBadge>
  );
}

export function ExceptionStatusBadge({ value, testId }) {
  const t = EXC_TONE[value] || EXC_TONE.NONE;
  return (
    <StatusBadge tone={t.tone} testId={testId}>
      {t.l}
    </StatusBadge>
  );
}

export function DeadlineBadge({ days, testId }) {
  if (days === null || days === undefined) return null;
  let tone = "neutral";
  let label;
  if (days < 0) { tone = "danger"; label = `Lewat ${Math.abs(days)} hari`; }
  else if (days <= 1) { tone = "danger"; label = days === 0 ? "Hari ini" : "Besok (1 hari)"; }
  else if (days <= 3) { tone = "warning"; label = `${days} hari lagi`; }
  else { tone = "success"; label = `${days} hari lagi`; }
  return (
    <StatusBadge tone={tone} testId={testId}>
      {label}
    </StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// Q68-01 Batch 2 — tiga kelompok status yang selama ini HANYA punya warna di
// jalur keras (`lib/statusLabels.js:badgeColor`). Ditambahkan di sini supaya
// Batch 3-5 tinggal memindahkan halaman; **belum ada satu pun pemakainya di
// batch ini** — sengaja, supaya nol layar berubah sebelum peta ini diperiksa.
//
// Warna diambil dari GAP-B yang sudah disetujui Owner (BACKLOG `Q68-01
// SUB-RENCANA`). `badgeColor()` mewarnai lewat TEBAKAN STRING
// (`.includes("MENUNGGU")`), dan tebakan itu meleset di tujuh state; peta di
// bawah memakai tone A2 yang benar, jadi memindahkan halaman ke sini MEMANG
// mengubah warna di layar. Itu perbaikan yang disengaja, bukan efek samping.
//
// Label ikut disimpan di sini (pola `FA_ORDER_STATUS`, bukan penemuan baru).
// Alasannya bukan kerapian: `lib/statusLabels.js` sudah meng-import berkas
// ini untuk `FA_ORDER_STATUS`, jadi mengambil label dari arah sebaliknya
// akan membuat siklus import antar-dua modul yang sama-sama mengekspor const
// top-level. Di Batch 4-6 `DR_STATUS_LABEL`/`ADJUSTMENT_STATUS_LABEL`/
// `TASK_STATUS_LABEL` DITURUNKAN dari peta ini persis seperti
// `FA_STATUS_LABEL` hari ini — bukan disalin jadi sumber ketiga.
// Sampai itu terjadi, label di bawah disalin PERSIS dari sumber lamanya.
// ---------------------------------------------------------------------------

// design_requests.dr_status (9 state) — label PERSIS dari
// `lib/statusLabels.js:DR_STATUS_LABEL`.
//
// Dua state berubah warna dibanding `badgeColor()` hari ini:
//   CANCELLED        rose/MERAH -> neutral  (A2: dibatalkan = netral)
//   DRAFT_FA_CREATED sky/BIRU   -> neutral  (draft = netral, sejajar
//                                            `FA_ORDER_STATUS` di atas)
// Tujuh sisanya tone-nya sama dengan yang sudah tampil, cuma sumbernya
// pindah dari palet keras ke token.
export const DR_STATUS_TONE = {
  DESIGN_REQUEST_CREATED: { tone: "neutral", l: "Permintaan Desain Dibuat" },
  MENUNGGU_TANDA_JADI: { tone: "warning", l: "Belum Bayar Tanda Jadi" },
  TANDA_JADI_MENUNGGU_KONFIRMASI: { tone: "warning", l: "Tanda Jadi Menunggu Konfirmasi" },
  TANDA_JADI_CONFIRMED: { tone: "success", l: "Tanda Jadi Dikonfirmasi" },
  DESIGN_IN_PROGRESS: { tone: "info", l: "Desain Sedang Dikerjakan" },
  DESIGN_REVISION_REQUESTED: { tone: "warning", l: "Desain Minta Revisi" },
  DESIGN_APPROVED: { tone: "success", l: "Desain Final Disetujui" },
  DRAFT_FA_CREATED: { tone: "neutral", l: "Draf FA Sudah Dibuat" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// Special adjustment (5 state) — label PERSIS dari
// `lib/statusLabels.js:ADJUSTMENT_STATUS_LABEL`.
//
// EMPAT dari lima berubah, dan ini kelompok yang paling rusak hari ini:
// `APPROVED` dan `REJECTED` sama-sama jatuh ke fallback biru `badgeColor()`,
// jadi "Disetujui" dan "Ditolak" tampil DENGAN WARNA YANG SAMA PERSIS di
// FADetail. Dua state `PENDING_*` juga biru padahal artinya menunggu —
// tebakan `.includes("MENUNGGU")` tidak kena karena kuncinya berbahasa
// Inggris.
//
// ⚠ Selisih yang DISADARI dan belum diputuskan: `EXC_TONE.APPROVED` dan
// `PR_STATUS_TONE.DIKONFIRMASI` di berkas ini memakai `success-solid` untuk
// makna "disetujui/dikonfirmasi" yang sama. Di sini dipakai `success`
// (lembut) karena itu yang tertulis di tabel GAP-B yang disetujui Owner.
// Menyamakannya butuh keputusan tersendiri — dicatat, tidak diselipkan.
export const ADJUSTMENT_STATUS_TONE = {
  PENDING_OWNER_APPROVAL: { tone: "warning", l: "Menunggu Persetujuan Owner" },
  PENDING_KACAB_APPROVAL: { tone: "warning", l: "Menunggu Persetujuan Kepala Cabang" },
  APPROVED: { tone: "success", l: "Disetujui" },
  REJECTED: { tone: "danger", l: "Ditolak" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// design_tasks.status (4 state) — label PERSIS dari `TASK_STATUS_LABEL`, yang
// hari ini DIDEFINISIKAN DUA KALI (`pages/DesignTasks.jsx:10` dan
// `pages/DesignTaskDetail.jsx:13`). Batch 5 menghapus keduanya dan menurunkan
// dari sini; sampai saat itu ketiganya wajib berbunyi sama.
//
// Dua state berubah warna:
//   COMPLETED sky/BIRU  -> success  (selesai = hijau)
//   CANCELLED rose/MERAH-> neutral
// `OPEN` dibiarkan `info` — bukan karena sudah pasti benar, tapi karena tabel
// GAP-B tidak mencantumkannya sebagai perubahan, dan batch ini tidak
// menambah perubahan warna di luar yang disetujui.
export const DESIGN_TASK_STATUS_TONE = {
  OPEN: { tone: "neutral", l: "Belum Dikerjakan" },
  CLAIMED: { tone: "info", l: "Sedang Dikerjakan" },
  COMPLETED: { tone: "success", l: "Selesai" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// `bordered` DEFAULT FALSE — sama dengan `StatusBadge` generik, jadi SEMUA
// badge ETAMSIOS kini satu bentuk.
//
// Batch 2 memasang default TRUE di sini dengan alasan "enam halaman tujuan
// punya cincin hari ini, jadi migrasinya setia secara visual". Alasan itu
// runtuh begitu Batch 3-4 benar-benar memakainya: ketiga pemakai pertama
// menulis `bordered={false}` eksplisit, nol yang memakai default. Sebabnya
// satu layar — `FAStatusBadge` dan `PRStatusBadge` tidak bercincin (keduanya
// lewat default `StatusBadge` generik, sama seperti CustomerDetail dan 12
// halaman purchasing), jadi default TRUE menaruh SATU badge bercincin di
// antara badge-badge polos pada layar yang sama.
//
// Yang dikejar Batch 2 — kesetiaan pada tampilan lama — kalah dari yang
// dikejar Q68-01 secara keseluruhan: satu kosakata badge, satu bentuk.
// Cincin tetap tersedia lewat prop untuk pemakai yang memang butuh.
// Keputusan Owner, 2026-08-20 (GAP-C opsi (a), Batch 5 Bagian A).

/** Badge `design_requests.dr_status`. Kode tak dikenal tampil apa adanya —
 *  pola persis `FAStatusBadge`: drift DB dibiarkan terlihat, bukan
 *  disembunyikan jadi "—". */
export function DRStatusBadge({ value, testId, variant, bordered = false }) {
  const t = DR_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId} variant={variant} bordered={bordered}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

/** Badge status special adjustment. */
export function AdjustmentStatusBadge({ value, testId, variant, bordered = false }) {
  const t = ADJUSTMENT_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId} variant={variant} bordered={bordered}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// Reject / Remake (Q68-01 Batch 7). Sumber daftar nilai adalah CHECK
// `wo_reject_remake_status_check` dan `wo_reject_remake_type_check`
// (`009b_phase1c_wo_reject_remake.sql:181-185` dan `:153-156`) — bukan peta JS
// mana pun. Keempat status dan kedua tipe hidup di
// `routers/wo_reject_remake.py`.
//
// Label disalin PERSIS dari `STATUS_META`/`TYPE_META` di
// `lib/rejectRemakeApi.js` sebelum peta itu dihapus, jadi teksnya tidak
// berubah sedikit pun bagi pemakai.
//
// `REPORTED` dan `REMAKE` memakai dua tone pengecualian — lihat catatan
// panjang di `TONE_CLASSES`. Sisanya tone A2 biasa: `IN_REMAKE` = sedang
// berjalan (info), `RESOLVED` = selesai (success), `CANCELLED` = dibatalkan
// (neutral), `REJECT` = butuh tindakan/bermasalah (danger).
export const RR_STATUS_TONE = {
  REPORTED: { tone: "reported", l: "Dilaporkan" },
  IN_REMAKE: { tone: "info", l: "Sedang Remake" },
  RESOLVED: { tone: "success", l: "Selesai" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

export const RR_TYPE_TONE = {
  REJECT: { tone: "danger", l: "REJECT" },
  REMAKE: { tone: "remake", l: "REMAKE" },
};

/** Badge `wo_reject_remake.status`. Fallback ke `REPORTED` mempertahankan
 *  perilaku lama `RejectRemakeSection.jsx:167` apa adanya. */
export function RRStatusBadge({ value, testId, bordered = false }) {
  const t = RR_STATUS_TONE[value] || RR_STATUS_TONE.REPORTED;
  return (
    <StatusBadge tone={t.tone} testId={testId} bordered={bordered}>
      {t.l}
    </StatusBadge>
  );
}

/** Badge `wo_reject_remake.type`. Fallback ke `REJECT`, sama seperti
 *  perilaku lama di `:166`. */
export function RRTypeBadge({ value, testId, bordered = false }) {
  const t = RR_TYPE_TONE[value] || RR_TYPE_TONE.REJECT;
  return (
    <StatusBadge tone={t.tone} testId={testId} bordered={bordered}>
      {t.l}
    </StatusBadge>
  );
}

/** Badge `design_tasks.status`. */
export function DesignTaskStatusBadge({ value, testId, variant, bordered = false }) {
  const t = DESIGN_TASK_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId} variant={variant} bordered={bordered}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// KONTRAS-01 Batch 8 (KONTRAS-FOC-SC) — dua kelompok status yang sampai hari
// ini menyimpan petanya SENDIRI di halaman, dalam bentuk `cls:` warna keras:
// `pages/FocRequests.jsx:17` dan `pages/SpecialCustomers.jsx:26`. Keduanya
// tidak pernah ikut Batch 4-7 Q68-01, jadi badge-nya tetap bergaya mode
// terang di dalam UI gelap (entri BACKLOG `KONTRAS-FOC-SC`).
//
// Label disalin PERSIS dari peta lama — nol teks berubah bagi pemakai.
// Tone mengikuti A2 dan SAMA PERSIS dengan `ADJUSTMENT_STATUS_TONE` di atas
// untuk keempat kunci yang beririsan, supaya "Menunggu Owner" tidak berbunyi
// satu warna di Special Adjustment dan warna lain di FOC.
//
// Rona yang tampil tidak bergeser: amber tetap amber, emerald tetap emerald,
// rose tetap rose, slate tetap slate. Yang berubah cuma SUMBERNYA — dari
// palet keras jadi token, sehingga ikut bertukar saat tema berganti.
// ---------------------------------------------------------------------------

// foc_requests.status (4 state).
export const FOC_STATUS_TONE = {
  PENDING_OWNER_APPROVAL: { tone: "warning", l: "Menunggu Owner" },
  APPROVED: { tone: "success", l: "Disetujui" },
  REJECTED: { tone: "danger", l: "Ditolak" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// special_customers.status (4 state). Bedanya dengan FOC cuma state terakhir:
// `REVOKED` ("Dicabut") menggantikan `CANCELLED`. Sengaja peta terpisah, bukan
// satu peta gabungan — dua tabel berbeda dengan daftar state berbeda, dan
// menggabungkannya akan membuat satu kode hidup di modul yang tidak
// mengenalnya.
export const SC_STATUS_TONE = {
  PENDING_OWNER_APPROVAL: { tone: "warning", l: "Menunggu Owner" },
  APPROVED: { tone: "success", l: "Disetujui" },
  REJECTED: { tone: "danger", l: "Ditolak" },
  REVOKED: { tone: "neutral", l: "Dicabut" },
};

/** Badge `foc_requests.status`. Kode tak dikenal tampil apa adanya dengan tone
 *  netral — pola persis `FAStatusBadge`, dan setia pada perilaku lama
 *  (`FocRequests.jsx:105` juga jatuh ke abu netral + label kode mentah). */
export function FOCStatusBadge({ value, testId }) {
  const t = FOC_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

/** Badge `special_customers.status`. */
export function SCStatusBadge({ value, testId }) {
  const t = SC_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// Inventory (KONTRAS-01 Batch 9). Empat peta `STATUS_BADGE` lokal ber-`cls:`
// warna keras — satu di tiap halaman inventory — dilebur ke sini, jalur yang
// sama dengan FOC/SC hasil Batch 8.
//
// ⚠ PREFIKS `INV_` BUKAN HIASAN. `ADJUSTMENT_STATUS_TONE` di atas milik
// SPECIAL ADJUSTMENT FA (`FADetail.jsx:226`) — modul yang sama sekali
// berbeda, dengan daftar state berbeda (`PENDING_OWNER_APPROVAL` dst.).
// `pages/inventory/Adjustments.jsx` adalah STOCK adjustment. Dua "adjustment"
// yang tidak berhubungan; menggabungkan petanya akan membuat kode dari satu
// modul hidup di modul yang tidak mengenalnya — kesalahan yang sudah dihindari
// eksplisit saat FOC/SC dipisah di Batch 8.
//
// Label disalin PERSIS dari peta lama — nol teks berubah bagi pemakai.
//
// ⚠ SATU RONA SENGAJA BERGESER: `CANCELLED` di Material Issues, Opname, dan
// Transfers hari ini `rose` (MERAH); di sini `neutral`. Itu bukan pilihan
// baru melainkan keputusan repo yang sudah berlaku di empat tempat —
// `ADJUSTMENT_STATUS_TONE.CANCELLED`, `FOC_STATUS_TONE.CANCELLED`,
// `SC_STATUS_TONE.REVOKED`, dan `DESIGN_TASK_STATUS_TONE` yang mencatat
// "CANCELLED rose/MERAH -> neutral" verbatim. Ketiga halaman inventory ini
// yang menyimpang, bukan sebaliknya. "Dibatalkan" bukan "gagal".
// ---------------------------------------------------------------------------

// inventory_adjustments.status (4 state) — STOCK adjustment, bukan FA.
export const INV_ADJUSTMENT_STATUS_TONE = {
  DRAFT: { tone: "neutral", l: "Draf" },
  APPROVED: { tone: "success", l: "Disetujui" },
  REJECTED: { tone: "danger", l: "Ditolak" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// material_issues.status (4 state).
export const INV_MATERIAL_ISSUE_STATUS_TONE = {
  DRAFT: { tone: "neutral", l: "Draf" },
  SUBMITTED: { tone: "warning", l: "Diajukan" },
  APPROVED: { tone: "success", l: "Disetujui" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// stock_opname.status (4 state).
//
// ⛔ KUNCI KEDUA DIPERBAIKI: `COUNTING` -> `IN_PROGRESS` (OPNAME-COUNTING-01).
// `COUNTING` adalah kode yang TIDAK PERNAH ADA di sisi mana pun kecuali
// frontend — sapuan penuh backend menghasilkan NOL kemunculan. Kedua sumber
// otoritatif sepakat `IN_PROGRESS`, dan keduanya diukur bukan diingat:
//   backend/migrations/010a_phase1d_inventory_core.sql:208
//     check (status in ('DRAFT','IN_PROGRESS','FINALIZED','CANCELLED'))
//   backend/services/inventory_service.py:590
//     update({"status": "IN_PROGRESS"})  <- yang benar-benar ditulis saat start
//
// Akibat sebelum perbaikan ini BUKAN sekadar kosmetik: `COUNTING` nol pernah
// cocok, jadi sesi opname yang sedang berjalan jatuh ke fallback dan tampil
// sebagai KODE MENTAH `IN_PROGRESS` ber-rona ABU — K73 (sedang berjalan =
// BIRU) terbaca terbalik jadi K81 (tidak sedang berjalan). Rona `info` di
// bawah karena itu BUKAN rona yang bergeser; ia rona yang akhirnya SAMPAI.
//
// ⚠ Kelas `cacat sambungan lintas-sisi` (§3b serah terima sesi 7): test
// backend memanggil path backend lalu lulus, test frontend nol ada, dan
// SAMBUNGAN di antaranya nol diuji. Yang menemukannya sensus kontrak DUA
// ARAH atas 19 peta di berkas ini, bukan satu pun dari 934 test.
//
// ⚠ Nama `stock_opnames` (jamak) di komentar lama juga keliru — tabelnya
// `stock_opname`, tunggal. Begitu pula klaim "sekeluarga dengan `OPEN` di
// DESIGN_TASK_STATUS_TONE yang juga `info`": K85 sudah memindahkan `OPEN` ke
// `neutral` + "Belum Dikerjakan", jadi kalimat itu sudah basi saat dibaca.
//
// Label "Sedang Dihitung" menggantikan "Berjalan" — sekeluarga dengan
// "Sedang Dikerjakan" (WO_STAGE / DESIGN_TASK) dan "Sedang Remake" (RR), dan
// menyebut apa yang sebenarnya terjadi. "Berjalan" sudah bahasa Indonesia;
// ini penajaman, bukan perbaikan cacat.
export const INV_OPNAME_STATUS_TONE = {
  DRAFT: { tone: "neutral", l: "Draf" },
  IN_PROGRESS: { tone: "info", l: "Sedang Dihitung" },
  FINALIZED: { tone: "success", l: "Selesai" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// stock_transfers.status (4 state).
export const INV_TRANSFER_STATUS_TONE = {
  DRAFT: { tone: "neutral", l: "Draf" },
  DISPATCHED: { tone: "info", l: "Dikirim" },
  RECEIVED: { tone: "success", l: "Diterima" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

/** Badge status stock adjustment (`pages/inventory/Adjustments.jsx`).
 *
 *  ⚠ Kode tak dikenal: tone `neutral` + kode mentah. Perilaku LAMA memakai
 *  `cls: ""`, yang membuat `<Badge>` jatuh ke varian default `bg-primary` —
 *  status asing tampil NAVY SOLID, sewarna tombol utama. Netral lebih jujur
 *  dan sejalan dengan `FOCStatusBadge`. Berlaku untuk keempat badge di bawah. */
export function InvAdjustmentStatusBadge({ value, testId }) {
  const t = INV_ADJUSTMENT_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

/** Badge `material_issues.status`. */
export function MaterialIssueStatusBadge({ value, testId }) {
  const t = INV_MATERIAL_ISSUE_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

/** Badge `stock_opnames.status`. */
export function OpnameStatusBadge({ value, testId }) {
  const t = INV_OPNAME_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

/** Badge `stock_transfers.status`. */
export function TransferStatusBadge({ value, testId }) {
  const t = INV_TRANSFER_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

// Badge TIPE pergerakan stok (`StockLedger.jsx`, `FGMovement.jsx`). Tiga
// makna -> tiga tone, pemetaan satu-lawan-satu: NOL informasi hilang.
//
// ⚠ Ini badge ARAH, bukan badge STATUS — kelas informasi yang Owner pisahkan
// 23 Agustus 2026 saat mengeluarkan badge arah `Transfers.jsx:77-81` dari
// batch ini. Yang dikeluarkan di sana adalah pemetaan 4 makna -> 2 tone
// (kehilangan informasi); di sini 3 -> 3, jadi substitusi tokennya setia.
// Rona tidak bergeser: emerald tetap hijau, rose tetap merah, slate tetap abu.
//
// `danger` untuk OUT adalah nama tone, BUKAN klaim bahwa stok keluar itu
// galat — ia mewarisi rose yang sudah dipakai kode lama. Kalau kosakata arah
// nanti pindah ke ikon/outline (arahan Owner), tempat ini ikut pindah.
export const INV_MOVEMENT_TYPE_TONE = { IN: "success", OUT: "danger", OTHER: "neutral" };

/** Kelas token untuk badge tipe pergerakan. Mengembalikan STRING kelas —
 *  bukan komponen — karena kedua pemanggilnya merender `<Badge className={...}>`
 *  sendiri dan bentuk itu dipertahankan apa adanya. */
export function movementTypeClass(kind) {
  return TONE_CLASSES[INV_MOVEMENT_TYPE_TONE[kind] || "neutral"] || TONE_CLASSES.neutral;
}

// ---------------------------------------------------------------------------
// Receivables (KONTRAS-01 Batch 10). Peta `STATUS_BADGE` lokal ber-`cls:` warna
// keras di `pages/Receivables.jsx:16` — jalur yang sama dengan FOC/SC (Batch 8)
// dan keempat peta inventory (Batch 9).
//
// Halaman ini juga tempat terakhir pola bug `J3-KONTRAS-01` (d) hidup:
// `sb.cls + " hover:" + sb.cls`. Prefiks hover hanya menempel di kelas PERTAMA
// hasil sambungan, jadi kelas kedua terduplikasi telanjang. Memakai komponen di
// bawah menghapus dua sisa terakhirnya sekaligus.
//
// Label disalin PERSIS dari peta lama — nol teks berubah bagi pemakai.
//
// ⚠ `SETTLED` "Lunas" memakai `success` LEMBUT, bukan varian solid — menyimpang
// dari `FA_ORDER_STATUS.LUNAS` yang solid. Alasannya kini TINGGAL SATU, dan
// bukan lagi soal kontras: ia sejajar dengan `ADJUSTMENT_STATUS_TONE`/
// `FOC_STATUS_TONE`/`SC_STATUS_TONE` hasil Batch 8-9, yang semuanya lembut.
//
// Alasan KEDUA yang tertulis di sini saat Batch 10 — bahwa varian solid gagal
// ambang AA teks di tema terang — SUDAH TIDAK BERLAKU. Kegagalan itu diperbaiki
// di definisi tokennya (`J3-SUCCESS-SOLID-01`, `index.css`: terang 30% -> 28%),
// bukan di pemakainya, jadi varian solid kini lulus di kedua tema. Menyamakan
// "Lunas" piutang dengan "Lunas" FA sekarang tinggal pertanyaan kosakata untuk
// Owner, bukan halangan teknis.
// ---------------------------------------------------------------------------

// receivables.status_api (5 state).
export const RECEIVABLE_STATUS_TONE = {
  OPEN: { tone: "warning", l: "Terbuka" },
  PARTIAL: { tone: "warning", l: "Sebagian Dibayar" },
  OVERDUE: { tone: "danger", l: "Lewat Tempo" },
  SETTLED: { tone: "success", l: "Lunas" },
  WRITTEN_OFF: { tone: "neutral", l: "Dihapus-Buku" },
};

/** Badge `receivables.status_api`. Kode tak dikenal tampil apa adanya dengan
 *  tone netral — pola persis `FOCStatusBadge`, dan setia pada perilaku lama
 *  (`Receivables.jsx:113` juga jatuh ke abu netral + label kode mentah). */
export function ReceivableStatusBadge({ value, testId }) {
  const t = RECEIVABLE_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

// ---------------------------------------------------------------------------
// Work Order (KONTRAS-01 Batch 11). DUA peta identik ber-`cls:` warna keras —
// `pages/WOList.jsx:14` dan `pages/WODetail.jsx:19` — untuk SATU kolom yang
// sama (`work_orders.status`). Dilebur ke satu peta di sini.
//
// ⚠ CACAT MAKNA YANG DIPERBAIKI, bukan sekadar sumber warna dipindah.
// `WODetail` merender badge status WO DAN badge status TAHAP di layar yang
// sama, dan kedua palet keras itu memakai rona yang SAMA untuk makna yang
// BERLAWANAN:
//
//   rona hijau : status WO  -> IN_PROGRESS ("sedang jalan")
//                status tahap -> COMPLETED  ("sudah selesai")
//   rona biru  : status WO  -> LOCKED      ("belum jalan")
//                status tahap -> IN_PROGRESS ("sedang jalan")
//
// Jadi hijau di kepala halaman berarti "berjalan" sementara hijau di daftar
// tahap tepat di bawahnya berarti "selesai". Sesudah pemetaan ini kedua
// tingkat SEPAKAT: berjalan = keluarga info, selesai = success.
//
// ⚠ DUA HIJAU DI PETA LAMA SENGAJA TIDAK DIRUNTUHKAN JADI SATU TONE.
// `IN_PROGRESS` dan `COMPLETED` sama-sama hijau di palet lama (dua keluarga
// hijau yang berbeda tipis). Memetakan keduanya ke `success` karena sama-sama
// hijau akan membuat WO yang MASIH diproduksi tampil identik dengan yang sudah
// selesai. Tone dipilih dari MAKNA, bukan dari rona.
// ---------------------------------------------------------------------------

// work_orders.status (6 state).
//
// ⚠ `LOCKED` dan `IN_PROGRESS` memakai DUA TINGKAT KEKUATAN dari keluarga tone
// yang sama, bukan satu tone yang sama (keputusan Owner, 23 Agustus 2026).
// Bedanya bermakna operasional — Terkunci = belum mulai, Produksi = sedang
// jalan — dan Kepala Cabang membaca WOList sebagai ANTREAN KERJA, jadi dua
// keadaan itu harus terbedakan sekilas. Polanya meniru pasangan lembut/solid
// yang sudah ada, bukan mekanisme baru.
//
// Rasio varian info solid DIUKUR sebelum dipakai, bukan diasumsikan lulus —
// pelajaran `J3-SUCCESS-SOLID-01`, tempat varian success solid ternyata gagal
// AA dan harus diperbaiki di tokennya. Varian info solid lulus apa adanya:
// 4.63 terang / 6.84 gelap.
export const WO_STATUS_TONE = {
  DRAFT: { tone: "neutral", l: "Draf" },
  PENDING_KEPALA_CABANG: { tone: "warning", l: "Menunggu Kacab" },
  LOCKED: { tone: "info", l: "Terkunci (Siap)" },
  IN_PROGRESS: { tone: "info-solid", l: "Produksi" },
  COMPLETED: { tone: "success", l: "Selesai" },
  CANCELLED: { tone: "neutral", l: "Dibatalkan" },
};

// work_order_stages.status (4 state). Sengaja peta terpisah dari WO_STATUS_TONE:
// dua kolom berbeda di dua tabel berbeda, dan kode `IN_PROGRESS` yang sama
// justru punya tingkat kekuatan berbeda di keduanya (tahap tidak perlu
// dibedakan dari "siap", karena tahap tidak punya keadaan "siap").
// K86 (keputusan Owner, 24 Agustus 2026): kolom ini berhenti merender kode
// mentah, jadi peta ini sekarang membawa LABEL juga — bentuk { tone, l } yang
// sama dengan 19 peta lain di berkas ini. Tone-nya NOL bergeser; yang lahir
// hanya kolom labelnya.
//
// KEEMPATNYA WAJIB ADA DAN NOL LEBIH. Himpunannya TERTUTUP, dibuktikan dua
// sumber otoritatif yang saling mengonfirmasi — bukan dibaca dari kode UI:
//   backend/migrations/009a_phase1c_wo_core.sql:182
//     check (status in ('PENDING','IN_PROGRESS','COMPLETED','SKIPPED'))
//   backend/services/wo_service.py:61-66  STAGE_TRANSITIONS, empat kunci sama
// Status kelima berarti migration baru; kalau itu terjadi, peta ini ikut
// bertambah — dan sampai ia bertambah, fallback di woStageStatusLabel yang
// menahan supaya kolomnya tidak diam-diam kosong.
export const WO_STAGE_STATUS_TONE = {
  PENDING: { tone: "neutral", l: "Belum Mulai" },
  IN_PROGRESS: { tone: "info", l: "Sedang Dikerjakan" },
  COMPLETED: { tone: "success", l: "Selesai" },
  SKIPPED: { tone: "neutral", l: "Dilewati" },
};

/** Badge `work_orders.status`.
 *
 *  ⚠ Kode tak dikenal: tone `neutral` + kode mentah. Perilaku LAMA di kedua
 *  halaman memberi fallback berupa latar abu terang dari palet keras TANPA
 *  warna teks, jadi kode asing tampil di atas latar terang dengan teks putih
 *  varian default `Badge` — praktis tak terbaca. Netral memperbaikinya
 *  sekaligus. (Nama kelasnya tidak ditulis di sini: PAGAR 1.) */
export function WOStatusBadge({ value, testId }) {
  const t = WO_STATUS_TONE[value];
  return (
    <StatusBadge tone={t?.tone || "neutral"} testId={testId}>
      {t?.l || value || "—"}
    </StatusBadge>
  );
}

/** Kelas token untuk badge status TAHAP. Mengembalikan STRING kelas, bukan
 *  komponen, supaya `WODetail` tetap menyusun `<Badge>`-nya sendiri
 *  berdampingan dengan ikon yang mengambil warna dari peta yang sama.
 *
 *  ⚠ ALASAN LAMA SUDAH TIDAK BERLAKU dan sengaja tidak dibiarkan berdiri:
 *  dulu di sini tertulis bentuk STRING dipakai "karena pemanggilnya merender
 *  KODE MENTAH, bukan label". K86 menghentikan render kode mentah itu, jadi
 *  kalimat itu akan jadi keterangan yang salah kalau dibiarkan. */
export function woStageStatusClass(status) {
  return TONE_CLASSES[WO_STAGE_STATUS_TONE[status]?.tone || "neutral"] || TONE_CLASSES.neutral;
}

/** Label status TAHAP (K86). Sebelum ini `WODetail` menampilkan kode basis
 *  data apa adanya — PENDING / IN_PROGRESS / COMPLETED / SKIPPED — ke layar
 *  yang dipakai orang produksi.
 *
 *  FALLBACK SENGAJA KODE MENTAH, BUKAN KOSONG. Kalau suatu hari status kelima
 *  lahir tanpa peta ini ikut diperbarui, kolomnya menampilkan kodenya —
 *  jelek, dan justru itu gunanya: yang jelek KELIHATAN salah, sementara kolom
 *  kosong kelihatan benar dan lolos begitu saja. `"—"` hanya untuk nilai
 *  kosong/null, mengikuti pola `WOStatusBadge`. */
export function woStageStatusLabel(status) {
  return WO_STAGE_STATUS_TONE[status]?.l || status || "—";
}

/** Kelas warna TEKS untuk ikon tahap (`WODetail`), sekeluarga dengan
 *  `woStageStatusClass` supaya ikon dan badge di baris yang sama tidak pernah
 *  bercerita beda. Ambangnya SC 1.4.11 (3.0) karena ikon, bukan teks. */
export function woStageIconClass(status) {
  const tone = WO_STAGE_STATUS_TONE[status]?.tone || "neutral";
  return {
    neutral: "text-muted-foreground",
    info: "text-info-soft-foreground",
    success: "text-success-soft-foreground",
    warning: "text-warning-soft-foreground",
  }[tone];
}
